import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Wrench, Plus, Cog, Loader2, Package, TrendingDown, Calendar, User, ChevronDown, ChevronUp, Edit, Trash2, Car,
  Upload, Image, Mail, QrCode, X,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OTHER_MACHINE_ID = "OTHER";
const OTHER_MACHINE_NAME = "Other / General (Vehicle, Office, etc.)";

const MachineInventoryView = () => {
  const [loading, setLoading] = useState(true);
  const [machines, setMachines] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState("");
  const [machineSpares, setMachineSpares] = useState([]);
  const [spareDetails, setSpareDetails] = useState({});
  const [expandedSpare, setExpandedSpare] = useState(null);
  const [spareFilter, setSpareFilter] = useState("all"); // "all" | "available" | "used"

  // Spare CRUD
  const [showSpareDialog, setShowSpareDialog] = useState(false);
  const [editingSpare, setEditingSpare] = useState(null);
  const [spareForm, setSpareForm] = useState({ spare_name: "", quantity: "1", status: "Available" });
  const [savingSpare, setSavingSpare] = useState(false);
  const [deleteSpareInfo, setDeleteSpareInfo] = useState(null);

  // Image Upload
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [spareImageUrl, setSpareImageUrl] = useState(null);
  const fileInputRef = useRef(null);

  // QR / Email share
  const [showQrDialog, setShowQrDialog] = useState(false);
  const [qrSpare, setQrSpare] = useState(null);
  const [shareRecipient, setShareRecipient] = useState("");
  const [shareAddress, setShareAddress] = useState("");
  const [shareQty, setShareQty] = useState("");
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [showNewAddress, setShowNewAddress] = useState(false);
  const [newAddress, setNewAddress] = useState("");

  // Stock
  const [showAddStock, setShowAddStock] = useState(null);
  const [stockQty, setStockQty] = useState("");
  const [stockNotes, setStockNotes] = useState("");
  const [savingStock, setSavingStock] = useState(false);

  useEffect(() => { fetchMachines(); }, []);

  const fetchMachines = async () => {
    try {
      const res = await axios.get(`${API}/machines`);
      let list = res.data;
      // Ensure "OTHER" machine exists for general spares
      if (!list.find((m) => m.id === OTHER_MACHINE_ID)) {
        try {
          await axios.post(`${API}/machines`, { id: OTHER_MACHINE_ID, name: OTHER_MACHINE_NAME });
          const res2 = await axios.get(`${API}/machines`);
          list = res2.data;
        } catch (err) {
          // May already exist
        }
      }
      setMachines(list);
      if (!selectedMachine && list.length > 0) setSelectedMachine(list[0].id);
    } catch (err) {
      toast.error("Failed to load machines");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedMachine) {
      const machine = machines.find((m) => m.id === selectedMachine);
      setMachineSpares(machine?.spares || []);
      setSpareDetails({});
      setExpandedSpare(null);
    }
  }, [selectedMachine, machines]);

  const fetchSpareDetail = async (spareUuid) => {
    if (spareDetails[spareUuid] && expandedSpare === spareUuid) {
      setExpandedSpare(null);
      return;
    }
    if (spareDetails[spareUuid]) {
      setExpandedSpare(spareUuid);
      return;
    }
    try {
      const res = await axios.get(`${API}/machines/${selectedMachine}/spares/${spareUuid}/detail`);
      setSpareDetails((prev) => ({ ...prev, [spareUuid]: res.data }));
      setExpandedSpare(spareUuid);
    } catch (err) {
      toast.error("Failed to load spare detail");
    }
  };

  // ADD spare
  const handleOpenAddSpare = () => {
    setEditingSpare(null);
    setSpareForm({ spare_name: "", quantity: "1", status: "Available" });
    setImagePreview(null);
    setImageBase64(null);
    setSpareImageUrl(null);
    setShowSpareDialog(true);
  };

  // EDIT spare
  const handleOpenEditSpare = (spare) => {
    setEditingSpare(spare);
    setSpareForm({
      spare_name: spare.spare_name || spare.name || "",
      quantity: String(spare.quantity || 1),
      status: spare.status || "Available",
    });
    setImagePreview(spare.image_url ? spare.image_url.replace("/view", "/uc?export=view") : null);
    setImageBase64(null);
    setSpareImageUrl(spare.image_url || null);
    setShowSpareDialog(true);
  };

  // Handle image file selection
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be under 10MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImagePreview(ev.target.result);
      setImageBase64(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  // Upload image to Google Drive
  const uploadImage = async () => {
    if (!imageBase64) return spareImageUrl;
    setUploadingImage(true);
    try {
      const spareName = spareForm.spare_name || "spare";
      const timestamp = Date.now();
      const ext = imageBase64.includes("png") ? "png" : imageBase64.includes("webp") ? "webp" : "jpg";
      const filename = `SPARE_${selectedMachine}_${spareName.replace(/\s+/g, "_")}_${timestamp}.${ext}`;
      const res = await axios.post(`${API}/spares/upload-image`, {
        image_base64: imageBase64,
        filename,
      });
      setSpareImageUrl(res.data.image_url);
      return res.data.image_url;
    } catch (err) {
      toast.error("Failed to upload image to Drive");
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSaveSpare = async () => {
    if (!spareForm.spare_name.trim()) { toast.error("Spare name required"); return; }
    setSavingSpare(true);
    try {
      // Upload image if new one was selected
      let imageUrl = spareImageUrl;
      if (imageBase64) {
        imageUrl = await uploadImage();
      }

      const payload = {
        spare_name: spareForm.spare_name,
        name: spareForm.spare_name,
        quantity: parseInt(spareForm.quantity) || 1,
        status: spareForm.status,
        image_url: imageUrl || "",
      };

      if (editingSpare) {
        await axios.put(`${API}/machines/${selectedMachine}/spares/${editingSpare.id}`, payload);
        toast.success("Spare updated");
      } else {
        await axios.post(`${API}/machines/${selectedMachine}/spares`, payload);
        toast.success("Spare added");
      }
      setShowSpareDialog(false);
      setEditingSpare(null);
      setImagePreview(null);
      setImageBase64(null);
      setSpareImageUrl(null);
      await fetchMachines();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save spare");
    } finally {
      setSavingSpare(false);
    }
  };

  // DELETE spare
  const handleDeleteSpare = async () => {
    if (!deleteSpareInfo) return;
    try {
      await axios.delete(`${API}/machines/${selectedMachine}/spares/${deleteSpareInfo.id}`);
      toast.success("Spare deleted");
      await fetchMachines();
    } catch (err) {
      toast.error("Failed to delete spare");
    } finally {
      setDeleteSpareInfo(null);
    }
  };

  // ADD stock
  const handleAddStock = async (spareUuid) => {
    if (!stockQty || parseInt(stockQty) <= 0) { toast.error("Enter a valid quantity"); return; }
    setSavingStock(true);
    try {
      await axios.post(`${API}/machines/${selectedMachine}/spares/${spareUuid}/stock`, {
        quantity: parseInt(stockQty),
        notes: stockNotes,
      });
      toast.success("Stock added");
      setShowAddStock(null);
      setStockQty("");
      setStockNotes("");
      const res = await axios.get(`${API}/machines/${selectedMachine}/spares/${spareUuid}/detail`);
      setSpareDetails((prev) => ({ ...prev, [spareUuid]: res.data }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add stock");
    } finally {
      setSavingStock(false);
    }
  };

  const selectedMachineData = machines.find((m) => m.id === selectedMachine);
  const isOther = selectedMachine === OTHER_MACHINE_ID;

  // Open QR dialog for a spare
  const handleShowQr = async (spare) => {
    setQrSpare(spare);
    setShareRecipient("");
    setShareAddress("");
    setShareQty("");
    setShowNewAddress(false);
    setNewAddress("");
    setShowQrDialog(true);
    try {
      const res = await axios.get(`${API}/delivery-addresses`);
      setSavedAddresses(res.data || []);
    } catch (err) { /* ignore */ }
  };

  // Send email share via backend SMTP
  const [sendingEmail, setSendingEmail] = useState(false);

  const handleEmailShare = async () => {
    if (!qrSpare) return;
    if (!shareRecipient.trim() || !shareRecipient.includes("@")) { toast.error("Enter a valid recipient email"); return; }
    const finalAddress = showNewAddress ? newAddress.trim() : shareAddress;
    if (!finalAddress) { toast.error("Please select or enter a delivery address"); return; }
    if (!shareQty.trim()) { toast.error("Please enter quantity needed"); return; }

    setSendingEmail(true);
    try {
      // Save new address if entered
      if (showNewAddress && newAddress.trim()) {
        await axios.post(`${API}/delivery-addresses`, { address: newAddress.trim() });
      } else if (shareAddress) {
        // Bump usage count for existing address
        await axios.post(`${API}/delivery-addresses`, { address: shareAddress });
      }

      await axios.post(`${API}/spares/share-email`, {
        spare_name: qrSpare.spare_name || qrSpare.name || "",
        spare_id: qrSpare.spare_id || "",
        machine_name: selectedMachineData?.name || selectedMachine || "",
        image_url: qrSpare.image_url || "",
        address: finalAddress,
        quantity: shareQty,
        recipient: shareRecipient.trim(),
      });
      toast.success(`Email sent to ${shareRecipient.trim()}`);
      setShowQrDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-10 w-10 animate-spin text-amber-400" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Machine Selector */}
      <Card className="bg-black/60 border-2 border-amber-500/30">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-2">
              <Cog className="h-5 w-5 text-amber-400" />
              <Label className="text-amber-400 font-bold whitespace-nowrap">Select Machine:</Label>
            </div>
            <select
              value={selectedMachine}
              onChange={(e) => setSelectedMachine(e.target.value)}
              className="flex-1 h-10 px-3 rounded-md bg-gray-800 border border-gray-600 text-white"
              data-testid="machine-inv-select"
            >
              <option value="">Select a machine</option>
              {machines.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.id === OTHER_MACHINE_ID ? `${m.id} - ${OTHER_MACHINE_NAME}` : `${m.id} - ${m.name}`}
                </option>
              ))}
            </select>
            {selectedMachine && (
              <Button onClick={handleOpenAddSpare} className="bg-amber-500 hover:bg-amber-600 text-black font-bold" data-testid="add-spare-to-machine">
                <Plus className="h-4 w-4 mr-1" /> Add Spare
              </Button>
            )}
          </div>
          {isOther && (
            <p className="text-xs text-amber-400/70 mt-2 flex items-center gap-1">
              <Car className="h-3 w-3" /> Use this category for non-machine spares like vehicle tyres, office supplies, etc.
            </p>
          )}
        </CardContent>
      </Card>

      {!selectedMachine ? (
        <div className="text-center py-16">
          <Cog className="h-16 w-16 text-gray-700 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">Select a machine to view its spare inventory</p>
        </div>
      ) : (
        <>
          {/* Spare Filter Tabs */}
          {machineSpares.length > 0 && (
            <div className="flex items-center gap-2" data-testid="spare-filter-tabs">
              {[
                { key: "all", label: `All (${machineSpares.length})` },
                { key: "available", label: `Available (${machineSpares.filter(s => (s.status || "Available") === "Available").length})` },
                { key: "used", label: `In Use (${machineSpares.filter(s => s.status === "In Use").length})` },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setSpareFilter(tab.key)}
                  className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-full border transition-all ${
                    spareFilter === tab.key
                      ? "bg-amber-500 text-black border-amber-500"
                      : "bg-transparent text-gray-400 border-gray-600 hover:border-amber-500/50"
                  }`}
                  data-testid={`spare-filter-${tab.key}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          {machineSpares.length === 0 ? (
        <div className="text-center py-16">
          <Wrench className="h-16 w-16 text-gray-700 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No spares added for {isOther ? "General / Other" : selectedMachineData?.name}</p>
          <p className="text-gray-600 text-sm mt-1">Click "Add Spare" to add the first spare part</p>
        </div>
      ) : (
        <div className="space-y-3">
          {machineSpares
            .filter(spare => {
              if (spareFilter === "available") return (spare.status || "Available") === "Available";
              if (spareFilter === "used") return spare.status === "In Use";
              return true;
            })
            .map((spare) => {
            const detail = spareDetails[spare.id];
            const isExpanded = expandedSpare === spare.id;
            return (
              <Card key={spare.id} className="bg-black/40 border border-gray-700">
                <CardHeader className="cursor-pointer hover:bg-gray-800/50 transition-all py-3 px-4" onClick={() => fetchSpareDetail(spare.id)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                      <Wrench className="h-4 w-4 text-amber-400" />
                      <span className="font-mono text-xs text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded border border-cyan-500/20">{spare.spare_id}</span>
                      <CardTitle className="text-base text-white">{spare.spare_name || spare.name}</CardTitle>
                    </div>
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      {spare.image_url && (
                        <Button size="sm" variant="ghost" onClick={() => handleShowQr(spare)} className="text-purple-400 hover:text-purple-300 h-7 w-7 p-0" data-testid={`qr-spare-${spare.id}`} title="View QR / Share">
                          <QrCode className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {detail && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${detail.available > 0 ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
                          Avail: {detail.available}
                        </span>
                      )}
                      <Button size="sm" onClick={() => { setShowAddStock(spare.id); setStockQty(""); setStockNotes(""); }} className="bg-amber-600 hover:bg-amber-700 text-white h-7 text-xs" data-testid={`add-stock-${spare.id}`}>
                        <Plus className="h-3 w-3 mr-1" /> Stock
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleOpenEditSpare(spare)} className="text-blue-400 hover:text-blue-300 h-7 w-7 p-0" data-testid={`edit-spare-${spare.id}`}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setDeleteSpareInfo(spare)} className="text-red-400 hover:text-red-300 h-7 w-7 p-0" data-testid={`delete-spare-${spare.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>

                {isExpanded && detail && (
                  <CardContent className="pt-0 px-4 pb-4">
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 text-center">
                        <p className="text-xs text-green-400 font-bold uppercase">Total Added</p>
                        <p className="text-xl font-bold text-white">{detail.total_added}</p>
                      </div>
                      <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-center">
                        <p className="text-xs text-red-400 font-bold uppercase">Total Used</p>
                        <p className="text-xl font-bold text-white">{detail.total_used}</p>
                      </div>
                      <div className={`${detail.available > 0 ? 'bg-amber-500/10 border-amber-500/20' : 'bg-red-500/10 border-red-500/20'} border rounded-lg p-3 text-center`}>
                        <p className="text-xs text-amber-400 font-bold uppercase">Available</p>
                        <p className={`text-xl font-bold ${detail.available > 0 ? 'text-white' : 'text-red-400'}`}>{detail.available}</p>
                      </div>
                    </div>

                    {detail.stocks.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-bold text-amber-400 mb-2 flex items-center gap-1"><Package className="h-3.5 w-3.5" /> Stock Entries</h4>
                        <Table>
                          <TableHeader>
                            <TableRow className="border-gray-600">
                              <TableHead className="text-amber-400 text-xs">Stock ID</TableHead>
                              <TableHead className="text-amber-400 text-xs text-center">Qty</TableHead>
                              <TableHead className="text-amber-400 text-xs">Date</TableHead>
                              <TableHead className="text-amber-400 text-xs">Notes</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {detail.stocks.map((stk) => (
                              <TableRow key={stk.id} className="border-gray-600/50">
                                <TableCell><span className="font-mono text-xs text-cyan-400">{stk.stock_id}</span></TableCell>
                                <TableCell className="text-center text-white font-bold">{stk.quantity_added}</TableCell>
                                <TableCell className="text-gray-300 text-xs">{stk.date_added ? new Date(stk.date_added).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-"}</TableCell>
                                <TableCell className="text-gray-400 text-xs">{stk.notes || "-"}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}

                    {detail.usage_logs.length > 0 && (
                      <div>
                        <h4 className="text-sm font-bold text-red-400 mb-2 flex items-center gap-1"><TrendingDown className="h-3.5 w-3.5" /> Usage History</h4>
                        <Table>
                          <TableHeader>
                            <TableRow className="border-gray-600">
                              <TableHead className="text-red-400 text-xs">Date</TableHead>
                              <TableHead className="text-red-400 text-xs text-center">Qty Out</TableHead>
                              <TableHead className="text-red-400 text-xs">Person</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {detail.usage_logs.map((log) => (
                              <TableRow key={log.id} className="border-gray-600/50">
                                <TableCell className="text-gray-300 text-xs">
                                  <Calendar className="h-3 w-3 inline mr-1 text-gray-500" />
                                  {log.date_used ? new Date(log.date_used).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-"}
                                </TableCell>
                                <TableCell className="text-center text-red-400 font-bold">-{log.quantity_out}</TableCell>
                                <TableCell className="text-gray-300 text-xs">
                                  <User className="h-3 w-3 inline mr-1 text-gray-500" />
                                  {log.person_name}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}

                    {detail.stocks.length === 0 && detail.usage_logs.length === 0 && (
                      <p className="text-gray-500 text-center py-4">No stock entries yet. Add stock to start tracking.</p>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
      </>
      )}

      {/* Add/Edit Spare Dialog */}
      <Dialog open={showSpareDialog} onOpenChange={(open) => { if (!open) { setShowSpareDialog(false); setEditingSpare(null); setImagePreview(null); setImageBase64(null); } }}>
        <DialogContent className="bg-gray-900 border-amber-500/50 text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-amber-400 flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              {editingSpare ? "Edit Spare" : `Add Spare to ${isOther ? "General" : selectedMachineData?.name}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-300">Spare Name *</Label>
              <Input value={spareForm.spare_name} onChange={(e) => setSpareForm((p) => ({ ...p, spare_name: e.target.value }))} className="bg-gray-800 border-gray-600 text-white mt-1" placeholder={isOther ? "e.g., Vehicle Tyre, Office Chair" : "e.g., Bearing, Belt"} data-testid="inv-spare-name" />
            </div>
            <div>
              <Label className="text-gray-300">Initial Quantity</Label>
              <Input type="number" min="1" value={spareForm.quantity} onChange={(e) => setSpareForm((p) => ({ ...p, quantity: e.target.value }))} className="bg-gray-800 border-gray-600 text-white mt-1" data-testid="inv-spare-qty" />
            </div>
            <div>
              <Label className="text-gray-300">Status</Label>
              <select value={spareForm.status} onChange={(e) => setSpareForm((p) => ({ ...p, status: e.target.value }))} className="w-full h-10 px-3 rounded-md bg-gray-800 border border-gray-600 text-white mt-1" data-testid="inv-spare-status">
                <option value="Available">Available</option>
                <option value="In Use">In Use</option>
              </select>
            </div>

            {/* Image Upload */}
            <div>
              <Label className="text-gray-300 flex items-center gap-1"><Image className="h-3.5 w-3.5" /> Spare Image</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
                data-testid="inv-spare-image-input"
              />
              <div className="mt-2">
                {imagePreview ? (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Spare preview"
                      className="w-full h-40 object-contain rounded-lg border border-gray-600 bg-gray-800"
                      data-testid="inv-spare-image-preview"
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setImagePreview(null); setImageBase64(null); setSpareImageUrl(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                      className="absolute top-1 right-1 bg-red-600/80 hover:bg-red-700 text-white h-6 w-6 p-0 rounded-full"
                      data-testid="inv-spare-image-remove"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                    {spareImageUrl && (
                      <p className="text-xs text-green-400 mt-1 truncate">Uploaded to Drive</p>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-28 border-2 border-dashed border-gray-600 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-amber-500/50 hover:bg-gray-800/50 transition-all cursor-pointer"
                    data-testid="inv-spare-image-upload-btn"
                  >
                    <Upload className="h-6 w-6 text-gray-500" />
                    <span className="text-xs text-gray-400">Click to upload spare image</span>
                  </button>
                )}
                {imagePreview && !spareImageUrl && (
                  <Button
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    className="mt-2 bg-gray-700 hover:bg-gray-600 text-white text-xs"
                  >
                    Change Image
                  </Button>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowSpareDialog(false); setEditingSpare(null); setImagePreview(null); setImageBase64(null); }} className="border-gray-600 text-gray-300">Cancel</Button>
            <Button onClick={handleSaveSpare} disabled={savingSpare || uploadingImage} className="bg-amber-500 hover:bg-amber-600 text-black font-bold" data-testid="inv-spare-save">
              {uploadingImage ? "Uploading Image..." : savingSpare ? "Saving..." : editingSpare ? "Update" : "Add Spare"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Spare Confirmation */}
      <AlertDialog open={!!deleteSpareInfo} onOpenChange={() => setDeleteSpareInfo(null)}>
        <AlertDialogContent className="bg-gray-900 border-red-500/50 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">Delete Spare</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Delete "{deleteSpareInfo?.spare_name || deleteSpareInfo?.name}"? This will remove all stock entries and usage history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-600 text-gray-300 hover:bg-gray-800">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSpare} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Stock Dialog */}
      <Dialog open={!!showAddStock} onOpenChange={() => setShowAddStock(null)}>
        <DialogContent className="bg-gray-900 border-amber-500/50 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-amber-400 flex items-center gap-2"><Plus className="h-5 w-5" /> Add Stock Batch</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-300">Quantity *</Label>
              <Input type="number" min="1" value={stockQty} onChange={(e) => setStockQty(e.target.value)} className="bg-gray-800 border-gray-600 text-white mt-1" placeholder="e.g., 40" data-testid="inv-stock-qty" />
            </div>
            <div>
              <Label className="text-gray-300">Notes</Label>
              <Input value={stockNotes} onChange={(e) => setStockNotes(e.target.value)} className="bg-gray-800 border-gray-600 text-white mt-1" placeholder="e.g., Purchased from supplier" data-testid="inv-stock-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddStock(null)} className="border-gray-600 text-gray-300">Cancel</Button>
            <Button onClick={() => handleAddStock(showAddStock)} disabled={savingStock} className="bg-amber-500 hover:bg-amber-600 text-black font-bold" data-testid="inv-stock-save">
              {savingStock ? "Saving..." : "Add Stock"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code + Email Share Dialog */}
      <Dialog open={showQrDialog} onOpenChange={setShowQrDialog}>
        <DialogContent className="bg-gray-900 border-purple-500/50 text-white max-w-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-purple-400 flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              {qrSpare?.spare_name || qrSpare?.name || "Spare"} - QR Code
            </DialogTitle>
          </DialogHeader>
          {qrSpare?.image_url && (
            <div className="space-y-4 py-2">
              {/* QR Code */}
              <div className="flex flex-col items-center gap-3">
                <div className="bg-white p-3 rounded-lg" data-testid="spare-qr-code">
                  <QRCodeSVG value={qrSpare.image_url} size={180} level="H" />
                </div>
                <p className="text-xs text-gray-400 text-center break-all">
                  Scan to view spare image
                </p>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-700" />

              {/* Email Share */}
              <div className="space-y-3">
                <h4 className="text-sm font-bold text-amber-400 flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> Share via Email</h4>
                <div>
                  <Label className="text-gray-400 text-xs">Recipient Email *</Label>
                  <Input
                    type="email"
                    value={shareRecipient}
                    onChange={(e) => setShareRecipient(e.target.value)}
                    className="bg-gray-800 border-gray-600 text-white mt-1"
                    placeholder="Enter recipient email address"
                    data-testid="spare-share-recipient"
                  />
                </div>
                <div>
                  <Label className="text-gray-400 text-xs">Delivery Address *</Label>
                  {!showNewAddress ? (
                    <div className="space-y-2 mt-1">
                      <select
                        value={shareAddress}
                        onChange={(e) => setShareAddress(e.target.value)}
                        className="w-full h-10 px-3 rounded-md bg-gray-800 border border-gray-600 text-white"
                        data-testid="spare-share-address-select"
                      >
                        <option value="">Select saved address</option>
                        {savedAddresses.map((a, i) => (
                          <option key={i} value={a.address}>{a.address}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setShowNewAddress(true)}
                        className="text-xs text-amber-400 hover:text-amber-300 underline"
                        data-testid="spare-share-add-new-address"
                      >
                        + Add new address
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2 mt-1">
                      <Input
                        value={newAddress}
                        onChange={(e) => setNewAddress(e.target.value)}
                        className="bg-gray-800 border-gray-600 text-white"
                        placeholder="Enter new delivery address"
                        data-testid="spare-share-new-address"
                      />
                      {savedAddresses.length > 0 && (
                        <button
                          type="button"
                          onClick={() => { setShowNewAddress(false); setNewAddress(""); }}
                          className="text-xs text-gray-400 hover:text-gray-300 underline"
                        >
                          Select from saved addresses
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-gray-400 text-xs">Quantity Needed *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={shareQty}
                    onChange={(e) => setShareQty(e.target.value)}
                    className="bg-gray-800 border-gray-600 text-white mt-1"
                    placeholder="Enter quantity"
                    data-testid="spare-share-qty"
                  />
                </div>
                <Button
                  onClick={handleEmailShare}
                  disabled={sendingEmail}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold"
                  data-testid="spare-share-email-btn"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  {sendingEmail ? "Sending..." : "Send Email"}
                </Button>
                <p className="text-[10px] text-gray-500 text-center">Sent from info@challengepack.in</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MachineInventoryView;
